import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as moment from 'moment';

import { PeriodDateSearchFields } from '@app/services/summary.service';
import { TimezoneService } from '@main-application/management/pages/system-configuration/components/date-time-configuration/timezone.service';
import { FilterPeriodConfig, PeriodType } from '@main-application/shared/services/summary-report-settings.service';

import { InspectionsListConstants } from '../../inspections-list/constants';

export interface InspectionFilterDateTag {
  startDate: string;
  endDate: string;
  periodType?: PeriodType;
  periodTypeLabel?: string;
  periodDateSearchFields?: PeriodDateSearchFields;
}

@Injectable({
  providedIn: 'root',
})
export class InspectionDateFiltersService {
  private readonly currentDate = this.timezoneService.getCurrentDateOnly();
  private readonly TimeFrame = InspectionsListConstants.TimeFrame;

  defaultPeriodType = PeriodType.ThisMonth;

  constructor(private readonly timezoneService: TimezoneService, private route: ActivatedRoute) {}

  calculateDatesFromPeriodType(periodType: PeriodType): { startDate: string; endDate: string } {
    const momentDate = moment.utc(this.currentDate);
    let startDate: Date | null = null;
    let endDate: Date | null = null;

    const isCustomPeriod = periodType === PeriodType.Custom;

    if (periodType === undefined || periodType === null || isCustomPeriod) {
      return { startDate: '', endDate: '' };
    }

    switch (periodType) {
      case PeriodType.Last7Days:
        startDate = momentDate.clone().subtract(7, 'days').toDate();
        endDate = new Date(this.currentDate);
        break;
      case PeriodType.Last30Days:
        startDate = momentDate.clone().subtract(30, 'days').toDate();
        endDate = new Date(this.currentDate);
        break;
      case PeriodType.Last60Days:
        startDate = momentDate.clone().subtract(60, 'days').toDate();
        endDate = new Date(this.currentDate);
        break;
      case PeriodType.Last3Months:
        startDate = momentDate.clone().subtract(3, 'months').toDate();
        endDate = new Date(this.currentDate);
        break;
      case PeriodType.Last6Month:
        startDate = momentDate.clone().subtract(6, 'months').toDate();
        endDate = new Date(this.currentDate);
        break;
      case PeriodType.LastYear:
        startDate = momentDate.clone().subtract(1, 'year').startOf('year').toDate();
        endDate = momentDate.clone().subtract(1, 'year').endOf('year').toDate();
        break;
      case PeriodType.ThisWeek:
        startDate = momentDate.clone().startOf('week').toDate();
        endDate = momentDate.clone().endOf('week').toDate();
        break;
      case PeriodType.Next30Days:
        startDate = new Date(this.currentDate);
        endDate = momentDate.clone().add(30, 'days').toDate();
        break;
      case PeriodType.Next60Days:
        startDate = new Date(this.currentDate);
        endDate = momentDate.clone().add(60, 'days').toDate();
        break;
      case PeriodType.Next6Month:
        startDate = new Date(this.currentDate);
        endDate = momentDate.clone().add(6, 'months').toDate();
        break;
      case PeriodType.Next2Weeks:
        startDate = new Date(this.currentDate);
        endDate = momentDate.clone().add(2, 'weeks').toDate();
        break;
      case PeriodType.ThisMonth:
        startDate = momentDate.clone().startOf('month').toDate();
        endDate = momentDate.clone().endOf('month').toDate();
        break;
      case PeriodType.ThisQuarter:
        startDate = momentDate.clone().startOf('quarter').toDate();
        endDate = momentDate.clone().endOf('quarter').toDate();
        break;
      case PeriodType.ThisYear:
        startDate = momentDate.clone().startOf('year').toDate();
        endDate = momentDate.clone().endOf('year').toDate();
        break;
      case PeriodType.AllTime:
        startDate = null;
        endDate = null;
        break;
      default:
        return { startDate: '', endDate: '' };
    }

    return {
      startDate: startDate ? this.formatDate(startDate) : '',
      endDate: endDate ? this.formatDate(endDate) : '',
    };
  }

  calculateDatesFromTimeframe(
    timeframe: number,
    currentDate: Date = new Date()
  ): { startDate: string; endDate: string } {
    const momentDate = moment.utc(currentDate);
    let startDate: Date | null = null;
    let endDate: Date | null = null;

    const isCustomPeriod = timeframe === this.TimeFrame.Custom;

    if (!timeframe || isCustomPeriod) {
      return { startDate: '', endDate: '' };
    }

    switch (timeframe) {
      case this.TimeFrame.Week:
        startDate = momentDate.subtract(7, 'days').startOf('day').toDate();
        endDate = moment.utc(currentDate).endOf('day').toDate();
        break;
      case this.TimeFrame.Month:
        startDate = momentDate.subtract(30, 'days').startOf('day').toDate();
        endDate = moment.utc(currentDate).endOf('day').toDate();
        break;
      case this.TimeFrame.Month_3:
        startDate = momentDate.subtract(3, 'months').startOf('day').toDate();
        endDate = moment.utc(currentDate).endOf('day').toDate();
        break;
      case this.TimeFrame.Month_6:
        startDate = momentDate.subtract(6, 'months').startOf('day').toDate();
        endDate = moment.utc(currentDate).endOf('day').toDate();
        break;
      case this.TimeFrame.Year:
        startDate = moment.utc(`${momentDate.year() - 1}-01-01T00:00:00.000Z`).toDate();
        endDate = moment.utc(`${momentDate.year() - 1}-12-31T23:59:59.999Z`).toDate();
        break;
      case this.TimeFrame.ThisWeek:
        startDate = momentDate.startOf('week').startOf('day').toDate();
        endDate = momentDate.endOf('week').endOf('day').toDate();
        break;
      case this.TimeFrame.NextTwoWeeks:
        startDate = momentDate.startOf('day').toDate();
        endDate = momentDate.add(2, 'weeks').endOf('day').toDate();
        break;
      case this.TimeFrame.ThisMonth:
        startDate = momentDate.startOf('month').startOf('day').toDate();
        endDate = momentDate.endOf('month').endOf('day').toDate();
        break;
      case this.TimeFrame.ThisQuarter:
        startDate = momentDate.startOf('quarter').startOf('day').toDate();
        endDate = momentDate.endOf('quarter').endOf('day').toDate();
        break;
      case this.TimeFrame.ThisYear:
        startDate = momentDate.startOf('year').startOf('day').toDate();
        endDate = momentDate.endOf('year').endOf('day').toDate();
        break;
      case this.TimeFrame.AllTime:
        startDate = null;
        endDate = null;
        break;
      default:
        return { startDate: '', endDate: '' };
    }

    return {
      startDate: startDate ? this.formatDate(startDate) : '',
      endDate: endDate ? this.formatDate(endDate) : '',
    };
  }

  timeframeToPeriodType(timeframe: InspectionsListConstants.TimeFrame): PeriodType {
    switch (timeframe) {
      case this.TimeFrame.Week:
        return PeriodType.Last7Days;
      case this.TimeFrame.Month:
        return PeriodType.Last30Days;
      case this.TimeFrame.Month_3:
        return PeriodType.Last3Months;
      case this.TimeFrame.Month_6:
        return PeriodType.Last6Month;
      case this.TimeFrame.Year:
        return PeriodType.LastYear;
      case this.TimeFrame.AllTime:
        return PeriodType.AllTime;
      case this.TimeFrame.Custom:
        return PeriodType.Custom;
      case this.TimeFrame.ThisWeek:
        return PeriodType.ThisWeek;
      case this.TimeFrame.NextTwoWeeks:
        return PeriodType.Next2Weeks;
      case this.TimeFrame.ThisMonth:
        return PeriodType.ThisMonth;
      case this.TimeFrame.ThisQuarter:
        return PeriodType.ThisQuarter;
      case this.TimeFrame.ThisYear:
        return PeriodType.ThisYear;
      default:
        return PeriodType.Custom;
    }
  }

  generateDateTag(dateTagData: InspectionFilterDateTag): InspectionFilterDateTag | null {
    if (dateTagData?.periodType >= 0 && dateTagData.periodType !== PeriodType.Custom) {
      const { startDate, endDate } = this.calculateDatesFromPeriodType(dateTagData.periodType);
      return {
        periodType: dateTagData.periodType,
        periodTypeLabel: this.getPeriodTypeLabel(dateTagData.periodType),
        periodDateSearchFields: dateTagData.periodDateSearchFields,
        startDate,
        endDate,
      };
    }

    if (dateTagData.periodType === PeriodType.Custom) {
      return {
        startDate: dateTagData.startDate ? this.formatDate(dateTagData.startDate) : null,
        endDate: dateTagData.endDate ? this.formatDate(dateTagData.endDate) : null,
        periodDateSearchFields: dateTagData.periodDateSearchFields,
        periodType: PeriodType.Custom,
      };
    }

    const { startDate, endDate } = this.calculateDatesFromPeriodType(this.defaultPeriodType);
    return {
      startDate,
      endDate,
      periodDateSearchFields: PeriodDateSearchFields.CompletionDateOnly,
      periodType: this.defaultPeriodType,
      periodTypeLabel: this.getPeriodTypeLabel(this.defaultPeriodType),
    };
  }

  getDatesFromQueryParams(): InspectionFilterDateTag | null {
    const queryParams = { ...this.route.snapshot.queryParams };
    const timeframe = +queryParams.timeframe;
    const periodType = +queryParams.periodType;
    const notSetPeriodType = isNaN(periodType) && isNaN(timeframe) && !queryParams.startDate && !queryParams.endDate;
    if (notSetPeriodType && !queryParams.periodDateSearchFields) {
      return null;
    }

    if (notSetPeriodType && queryParams.periodDateSearchFields) {
      return {
        periodDateSearchFields: Number(queryParams.periodDateSearchFields),
        startDate: null,
        endDate: null,
      };
    }

    const periodTypeFromQuery = periodType >= 0 ? periodType : this.timeframeToPeriodType(timeframe);
    if (
      (timeframe === InspectionsListConstants.TimeFrame.Custom || periodTypeFromQuery === PeriodType.Custom) &&
      !queryParams.startDate &&
      !queryParams.endDate
    ) {
      return null;
    }

    let start, end;
    if (periodTypeFromQuery === PeriodType.Custom) {
      start = queryParams.startDate === '' ? null : queryParams.startDate || null;
      end = queryParams.endDate === '' ? null : queryParams.endDate || null;
    } else {
      const { startDate: startDateFromPeriodType, endDate: endDateFromPeriodType } =
        this.calculateDatesFromPeriodType(periodTypeFromQuery);
      start = startDateFromPeriodType;
      end = endDateFromPeriodType;
    }

    const periodDateSearchFields = Number(queryParams.periodDateSearchFields);
    const dateTag: InspectionFilterDateTag = {
      periodType: periodTypeFromQuery,
      periodTypeLabel: this.getPeriodTypeLabel(periodTypeFromQuery),
      startDate: start ? this.formatDate(start) : null,
      endDate: end ? this.formatDate(end) : null,
      periodDateSearchFields: periodDateSearchFields >= 0 ? periodDateSearchFields : 0,
    };
    return dateTag;
  }

  getPeriodTypeLabel(periodType: number): string {
    const period = FilterPeriodConfig.find(item => item.id === periodType);
    return period ? period.label : '';
  }

  formatDate(date: Date | string | null): string {
    if (!date) return '';

    const toYMD = (d: Date) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    if (date instanceof Date) {
      return toYMD(date);
    }

    if (typeof date === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }
      try {
        const d = new Date(date);
        if (isNaN(d.getTime())) return '';
        return toYMD(d);
      } catch {
        return '';
      }
    }
    return '';
  }
}
